import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const DangerZoneSection = ({ teacher }) => {
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const [showDialog, setShowDialog] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);

  const handleDelete = async () => {
    if (confirmText !== 'DELETE') return;
    setDeleting(true);
    setError(null);
    try {
      const { error: dbError } = await supabase
        ?.from('teachers')
        ?.delete()
        ?.eq('id', teacher?.id);
      if (dbError) throw dbError;
      const { error: authError } = await supabase?.rpc('delete_user');
      if (authError) {
        // Fallback: sign out if RPC not available
        console.warn('delete_user RPC not available, signing out instead.');
      }
      await signOut();
      navigate('/teacher-login', { replace: true });
    } catch (err) {
      setError(err?.message || 'Failed to delete account. Please contact support.');
      setDeleting(false);
    }
  };

  return (
    <div className="bg-card rounded-xl border-2 border-error/30 p-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-lg bg-error/10 flex items-center justify-center">
          <Icon name="AlertTriangle" size={20} className="text-error" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-error">Danger Zone</h2>
          <p className="text-sm text-muted-foreground">Irreversible and destructive actions</p>
        </div>
      </div>

      <div className="flex items-start justify-between gap-4 p-4 rounded-lg bg-error/5 border border-error/20">
        <div>
          <p className="text-sm font-medium text-foreground">Delete Account</p>
          <p className="text-xs text-muted-foreground mt-0.5">Permanently delete your teacher account, all classrooms, students, and data. This cannot be undone.</p>
        </div>
        <button
          onClick={() => setShowDialog(true)}
          className="flex-shrink-0 flex items-center gap-2 px-4 py-2 bg-error text-white rounded-lg text-sm font-medium hover:bg-error/90 transition-colors"
        >
          <Icon name="Trash2" size={16} />
          Delete Account
        </button>
      </div>

      {showDialog && (
        <div className="fixed inset-0 z-[2000] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => !deleting && setShowDialog(false)}
          />
          <div className="relative bg-card rounded-2xl shadow-2xl w-full max-w-md p-6 z-10">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-error/10 flex items-center justify-center">
                <Icon name="AlertTriangle" size={24} className="text-error" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground">Delete Account</h3>
                <p className="text-sm text-muted-foreground">This action is permanent and irreversible</p>
              </div>
            </div>

            <p className="text-sm text-foreground mb-4">
              All your classrooms, students, learning trails, and data will be permanently deleted.
              To confirm, type <span className="font-mono font-bold text-error">DELETE</span> below.
            </p>

            <input
              type="text"
              value={confirmText}
              onChange={e => setConfirmText(e?.target?.value)}
              placeholder="Type DELETE to confirm"
              className="w-full px-3 py-2.5 rounded-lg border border-border bg-background text-foreground text-sm font-mono focus:outline-none focus:ring-2 focus:ring-error/30 focus:border-error transition-colors mb-4"
            />

            {error && (
              <div className="flex items-center gap-2 text-sm px-3 py-2 rounded-lg bg-error/10 text-error mb-4">
                <Icon name="AlertCircle" size={16} />
                {error}
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => { setShowDialog(false); setConfirmText(''); setError(null); }}
                disabled={deleting}
                className="flex-1 px-4 py-2 rounded-lg border border-border text-sm font-medium hover:bg-muted transition-colors disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={confirmText !== 'DELETE' || deleting}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-error text-white rounded-lg text-sm font-medium hover:bg-error/90 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                {deleting ? <Icon name="Loader2" size={16} className="animate-spin" /> : <Icon name="Trash2" size={16} />}
                {deleting ? 'Deleting...' : 'Delete Forever'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DangerZoneSection;
